import { createMachine } from '@monofront/xstate-v4';
import { SELECT_SHIPPING_METHOD_TRANSITIONS } from './transitions';
import { SELECT_SHIPPING_METHOD_STATES } from './states';
import {
  SHIPPING_METHOD_TAG_PICKUP_SELECTED,
  SHIPPING_METHOD_TAG_DONE,
  SHIPPING_METHOD_TAG_COURIER_SELECTED,
} from './tags';
import {
  ISelectShippingMethodActions,
  ISelectShippingMethodContext,
  ISelectShippingMethodEvents,
  ISelectShippingMethodServices,
} from './types';
import {
  isCartMissing,
  isClickAndCollectFirstMethod,
  isClickAndCollectMethodSelected,
  isCourierFirstMethod,
  isCourierMethodSelected,
  isHomeFirstMethod,
  isHomeMethodSelected,
  isMethodSelected,
  isNoShipment,
  isPickupFirstMethod,
  isPickupMethodSelected,
  isPickupPointSelected,
} from './guards';
import {
  // assignPickupMethodSelected,
  assignShippingMethodsReceived,
  onFetchFailure,
  SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS,
  sendShippingMethodSelected,
  selectClickAndCollectMethodTransition,
  selectCourierMethodTransition,
  selectDaySlotTransition,
  selectHomeMethodTransition,
  selectPickupMethodTransition,
  selectPickupPointTransition,
  sendFetchToTimeSlotAction,
  assignFirstShippingMethodReceived,
  sendClearTimeSlotsSelectionAction,
  fetchShippingMethods,
  assignSetDeliveryInfos,
  cancelSendToParent,
  sendHomeToParent,
  sendCourierToParent,
  selectShippingAddressTransition,
  assignCheckoutCartId,
} from './actions';
import { SELECT_SHIPPING_METHOD_EXTERNAL_SERVICES } from './services';

export const SELECT_SHIPPING_METHOD_MACHINE_ID = 'select-shipping-method';

export const selectShippingMethodMachine = createMachine<
  ISelectShippingMethodContext,
  ISelectShippingMethodEvents
>({
  predictableActionArguments: true,
  id: SELECT_SHIPPING_METHOD_MACHINE_ID,
  initial: SELECT_SHIPPING_METHOD_STATES.INITIAL,
  schema: {
    context: {} as ISelectShippingMethodContext,
    events: {} as ISelectShippingMethodEvents,
    services: {} as ISelectShippingMethodServices,
    actions: {} as ISelectShippingMethodActions,
  },
  context: {
    methodSelectedCode: null,
    methodType: null,
    checkoutCartId: null,
    isFreshCart: false,
    isUltraFreshCart: false,
    shippingMethod: {
      shipmentMethodCode: null,
      deliveryDate: undefined,
      timeSlot: undefined,
      deliveryInstruction: undefined,
      pointId: undefined,
      address: undefined,
    },
    methodsList: [],
    selectPickupPointRef: null,
    selectTimeSlotsRef: null,
    noShipment: false,
    isUnpreselectShippingMethodABTestEnabled: false,
  },
  states: {
    [SELECT_SHIPPING_METHOD_STATES.INITIAL]: {
      entry: SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS.spawnMachines,
      always: [
        {
          target: SELECT_SHIPPING_METHOD_STATES.WAITING_FOR_CART,
          cond: isCartMissing,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.METHODS_FETCHING,
          cond: isMethodSelected,
        },
      ],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.WAITING_FOR_CART]: {
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SYNC_CART_DONE]: {
          target: SELECT_SHIPPING_METHOD_STATES.CART_READY,
          actions: [assignCheckoutCartId],
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.CART_READY]: {
      always: [
        {
          target: SELECT_SHIPPING_METHOD_STATES.METHODS_FETCHING,
          cond: isMethodSelected,
        },
      ],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.METHODS_FETCHING]: {
      invoke: {
        src: SELECT_SHIPPING_METHOD_EXTERNAL_SERVICES.fetchShippingMethods,
        onDone: [
          {
            target: SELECT_SHIPPING_METHOD_STATES.GET_SUCCESS,
            actions: [assignShippingMethodsReceived],
          },
        ],
        onError: {
          target: SELECT_SHIPPING_METHOD_STATES.GET_ERROR,
          actions: [
            onFetchFailure,
            SELECT_SHIPPING_METHOD_EXTERNAL_ACTIONS.fetchShippingMethodsError,
          ],
        },
      },
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SYNC_CART_DONE]: {
          actions: [assignCheckoutCartId],
        },
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.GET_SUCCESS]: {
      always: [
        {
          target: SELECT_SHIPPING_METHOD_STATES.COURIER_METHOD_SELECTED,
          cond: isCourierMethodSelected,
          actions: [sendFetchToTimeSlotAction],
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.HOME_METHOD_SELECTED,
          cond: isHomeMethodSelected,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.CLICK_AND_COLLECT_SELECTED,
          cond: isClickAndCollectMethodSelected,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.PICKUP_POINT_SELECTED,
          cond: isPickupPointSelected,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.PICKUP_METHOD_SELECTED,
          cond: isPickupMethodSelected,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.COURIER_METHOD_SELECTED,
          cond: isCourierFirstMethod,
          actions: [
            sendClearTimeSlotsSelectionAction,
            assignFirstShippingMethodReceived,
            sendFetchToTimeSlotAction,
          ],
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.HOME_METHOD_SELECTED,
          actions: [
            assignFirstShippingMethodReceived,
            sendShippingMethodSelected,
          ],
          cond: isHomeFirstMethod,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.CLICK_AND_COLLECT_SELECTED,
          actions: [
            assignFirstShippingMethodReceived,
            sendShippingMethodSelected,
          ],
          cond: isClickAndCollectFirstMethod,
        },
        {
          target: SELECT_SHIPPING_METHOD_STATES.PICKUP_METHOD_SELECTED,
          actions: assignFirstShippingMethodReceived,
          cond: isPickupFirstMethod,
        },
        { target: SELECT_SHIPPING_METHOD_STATES.GET_ERROR, cond: isNoShipment },
      ],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_CLICK_AND_COLLECT_METHOD]:
          selectClickAndCollectMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD]:
          selectPickupMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.GET_ERROR]: {
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.HOME_METHOD_SELECTED]: {
      tags: [SHIPPING_METHOD_TAG_DONE],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_CLICK_AND_COLLECT_METHOD]:
          selectClickAndCollectMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD]:
          selectPickupMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT]: {
          actions: [sendShippingMethodSelected],
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SET_DELIVERY_INFOS]: {
          actions: [
            assignSetDeliveryInfos,
            cancelSendToParent,
            sendHomeToParent,
          ],
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.CLICK_AND_COLLECT_SELECTED]: {
      tags: [SHIPPING_METHOD_TAG_DONE],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD]:
          selectPickupMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT]: {
          actions: [sendShippingMethodSelected],
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.PICKUP_METHOD_SELECTED]: {
      tags: [SHIPPING_METHOD_TAG_PICKUP_SELECTED],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_POINT]:
          selectPickupPointTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_CLICK_AND_COLLECT_METHOD]:
          selectClickAndCollectMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD]:
          selectPickupMethodTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.PICKUP_POINT_SELECTED]: {
      tags: [SHIPPING_METHOD_TAG_DONE, SHIPPING_METHOD_TAG_PICKUP_SELECTED],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_POINT]:
          selectPickupPointTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_CLICK_AND_COLLECT_METHOD]:
          selectClickAndCollectMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT]: {
          actions: sendShippingMethodSelected,
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.COURIER_METHOD_SELECTED]: {
      tags: [SHIPPING_METHOD_TAG_COURIER_SELECTED],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD]:
          selectPickupMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_CLICK_AND_COLLECT_METHOD]:
          selectClickAndCollectMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_TIME_SLOT]:
          selectDaySlotTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
      },
    },
    [SELECT_SHIPPING_METHOD_STATES.TIME_SLOT_SELECTED]: {
      tags: [SHIPPING_METHOD_TAG_COURIER_SELECTED, SHIPPING_METHOD_TAG_DONE],
      on: {
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_PICKUP_METHOD]:
          selectPickupMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.FETCH_METHODS]:
          fetchShippingMethods,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_HOME_METHOD]:
          selectHomeMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_CLICK_AND_COLLECT_METHOD]:
          selectClickAndCollectMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_TIME_SLOT]:
          selectDaySlotTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_COURIER_METHOD]:
          selectCourierMethodTransition,
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT]: {
          actions: sendShippingMethodSelected,
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SET_DELIVERY_INFOS]: {
          actions: [
            assignSetDeliveryInfos,
            cancelSendToParent,
            sendCourierToParent,
          ],
        },
        [SELECT_SHIPPING_METHOD_TRANSITIONS.SELECT_SHIPPING_ADDRESS]:
          selectShippingAddressTransition,
      },
    },
  },
});
